import React from "react";
import { Modal, ModalHeader, ModalFooter, ModalBody, Button } from "reactstrap";
import { ViewComponentPropType } from "@skyslit/ark-react";
import RankFileModule from "..";
import ReactTable from "react-table";
import { Link } from "react-router-dom";
import { RankFile, Subscription } from "lakshya-shared";
import { resolveAssetUrl } from "../../../shared/utils";

const defaultImage = require("./../assets/images/default-image.png");
const PAGE_SIZE = 10;

type DataFilter = (data: RankFile, index: number) => boolean;

type PropType = {
  isOpen: boolean;
  toggle: any;
  onSelect: (rankFiles: RankFile[]) => void;
  isMultiSelect?: boolean;
  filter: DataFilter;
};

export default function (
  props: ViewComponentPropType<RankFileModule> & PropType
) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState<RankFile[]>([]);
  const [pages, setPages] = React.useState(-1);
  const [page, setPage] = React.useState(0);
  const [selectedIds, updateSelectedIds] = React.useState([]);
  const searchRef = React.useRef("");
  const fetchKeyRef = React.useRef("");
  const fetchTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const dataFilter = props.filter || ((e: RankFile, i: number) => true);

  const isMultiSelect: boolean = props.isMultiSelect
    ? props.isMultiSelect
    : false;

  const handleFetch = React.useCallback(
    (nextPage: number, nextSearch: string) => {
      const key = `${nextPage}::${nextSearch}`;
      fetchKeyRef.current = key;
      searchRef.current = nextSearch;
      setPage(nextPage);
      setIsLoading(true);

      props.module.services
        .getAll({
          page: nextPage,
          limit: PAGE_SIZE,
          search: nextSearch,
        })
        .then((response: any) => {
          if (fetchKeyRef.current !== key) {
            return;
          }
          const rankFiles = (response?.rankFiles || []).filter(dataFilter);
          setData(rankFiles);
          setPages(response?.pages || 1);
          setPage(response?.page ?? nextPage);
          setIsLoading(false);
        })
        .catch((err) => {
          if (fetchKeyRef.current !== key) {
            return;
          }
          console.error(err);
          setIsLoading(false);
        });
    },
    [props.module.services, dataFilter]
  );

  const onFetchData = (state: any) => {
    if (!props.isOpen) {
      return;
    }

    const titleFilter = (state.filtered || []).find(
      (f: any) => f.id === "title"
    );
    const nextSearch = titleFilter?.value
      ? String(titleFilter.value).trim()
      : "";
    const searchChanged = nextSearch !== searchRef.current;
    const nextPage = searchChanged
      ? 0
      : typeof state.page === "number"
      ? state.page
      : 0;
    const key = `${nextPage}::${nextSearch}`;

    if (fetchKeyRef.current === key) {
      return;
    }

    if (fetchTimeout.current) {
      clearTimeout(fetchTimeout.current);
    }

    fetchTimeout.current = setTimeout(
      () => {
        handleFetch(nextPage, nextSearch);
      },
      searchChanged && nextSearch ? 400 : 0
    );
  };

  React.useEffect(() => {
    if (!props.isOpen) {
      fetchKeyRef.current = "";
      searchRef.current = "";
      setPage(0);
      setPages(-1);
      setData([]);
      updateSelectedIds([]);
    }
  }, [props.isOpen]);

  React.useEffect(() => {
    return () => {
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
    };
  }, []);

  const handleSelection = () => {
    props.onSelect &&
      props.onSelect(
        selectedIds.map((id) => {
          return data.find((e) => (e as any)._id === id);
        })
      );
    updateSelectedIds([]);
  };

  return (
    //@ts-ignore
    <Modal size="lg" isOpen={props.isOpen} toggle={props.toggle}>
      {/* @ts-ignore */}
      <ModalHeader toggle={props.toggle}>
        <i className="fas fa-scroll" /> Rank File Picker
      </ModalHeader>
      {/* @ts-ignore */}
      <ModalBody>
        {props.isOpen ? (
          <ReactTable
            manual
            loading={isLoading}
            data={data}
            pages={pages}
            page={page}
            pageSize={PAGE_SIZE}
            minRows={5}
            showPageJump={true}
            defaultPageSize={PAGE_SIZE}
            pageSizeOptions={[PAGE_SIZE]}
            onFetchData={onFetchData}
            columns={[
              {
                Header: "",
                accessor: "_id",
                width: 50,
                filterable: false,
                Cell: (row) => {
                  const id = row.original._id;
                  return (
                    <input
                      type="checkbox"
                      checked={selectedIds.indexOf(id) > -1}
                      onChange={(e) => {
                        if (e.target.checked === true) {
                          if (isMultiSelect === true) {
                            updateSelectedIds([...selectedIds, id]);
                          } else {
                            updateSelectedIds([id]);
                          }
                        } else {
                          updateSelectedIds(
                            selectedIds.filter((_id) => _id !== id)
                          );
                        }
                      }}
                    />
                  );
                },
              },
              {
                Header: "Title",
                accessor: "title",
                filterable: true,
                Cell: (row) => {
                  const rowData = row.original;
                  return (
                    <div className="d-flex">
                      <img
                        width={124}
                        src={
                          rowData.bannerImageId
                            ? resolveAssetUrl(rowData.bannerImageId, "public")
                            : defaultImage
                        }
                        alt="Video thumbnail"
                      />
                      <span className="ml-2">{row.value}</span>
                    </div>
                  );
                },
              },
              {
                Header: "Status",
                accessor: "isPublished",
                width: 100,
                filterable: false,
                Cell: (row) => {
                  return (
                    <span
                      className={`${
                        row.value === true
                          ? "text-success"
                          : "text-danger font-weight-bold"
                      }`}
                    >
                      {row.value === true ? "Published" : "Unpublished"}
                    </span>
                  );
                },
              },
              {
                Header: "Subscription",
                accessor: "masterSubscription",
                filterable: false,
                Cell: (row) => {
                  const subscription: Subscription = row.value;
                  if (subscription) {
                    return (
                      <Link
                        target="_blank"
                        className="text-success"
                        rel="noopener noreferrer"
                        to={`/admin/subscriptions/editor/${
                          (subscription as any)._id
                        }`}
                      >
                        {subscription.title}
                        <i className="fas fa-external-link-alt ml-3"></i>
                      </Link>
                    );
                  } else {
                    return <span className="text-muted">No Associations</span>;
                  }
                },
              },
            ]}
          />
        ) : null}
      </ModalBody>
      {/* @ts-ignore */}
      <ModalFooter>
        {/* @ts-ignore */}
        <Button color="secondary" onClick={props.toggle}>
          Cancel
        </Button>{" "}
        {/* @ts-ignore */}
        <Button
          disabled={selectedIds.length < 1}
          color="primary"
          onClick={handleSelection}
        >
          {selectedIds.length > 0
            ? `Select ${selectedIds.length} item(s)`
            : "Confirm Selection"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
